import React from "react";

import { motion, useReducedMotion } from "framer-motion";

import {
  Building2,
  Check,
  Headphones,
  Mail,
  MapPinned,
  Settings2,
  ShieldCheck,
  Wrench,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import LandingMenu from "../../../components/layout/LandingMenu";
import LoginButton from "../../../components/layout/LoginButton";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import LandingFooter from "../../../components/layout/LandingFooter";

import legupLogo from "../../../assets/legup-logo.svg";

import "./ContactPage.css";

/* =========================================================
   CONTACT ADDRESSES
   ========================================================= */

const ADMIN_EMAIL = "admin@renoassist.eu";

const SUPPORT_EMAIL = "support@renoassist.eu";

export default function ContactPage() {
  const { t } = useTranslation("home");

  const prefersReducedMotion = useReducedMotion();

  /* =========================================================
     HEADER ANIMATION
     ========================================================= */

  const headerVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : -16,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.58,

        staggerChildren: prefersReducedMotion ? 0 : 0.07,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const headerItemVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : -10,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.48,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =========================================================
     VIEWPORT ANIMATION

     Replays when elements enter / leave the viewport.
     ========================================================= */

  const revealVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 42,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.68,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 46,

      scale: prefersReducedMotion ? 1 : 0.985,
    },

    visible: (index = 0) => ({
      opacity: 1,

      y: 0,

      scale: 1,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.68,

        delay: prefersReducedMotion ? 0 : index * 0.08,

        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  const liveViewport = {
    once: false,

    amount: 0.18,

    margin: "-4% 0px -4% 0px",
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="contact-page">
      {/* =====================================================
          PUBLIC HEADER
          ===================================================== */}

      <motion.header
        className="contact-page__header"
        initial="hidden"
        animate="visible"
        variants={headerVariants}
      >
        <motion.div
          className="contact-page__header-left"
          variants={headerItemVariants}
        >
          <LandingMenu />
        </motion.div>

        <motion.a
          className="contact-page__brand"
          href="/"
          aria-label="LEG-UP"
          variants={headerItemVariants}
        >
          <img src={legupLogo} alt="LEG-UP" />
        </motion.a>

        <motion.div
          className="contact-page__header-right"
          variants={headerItemVariants}
        >
          <LanguageSwitcher />

          <LoginButton />
        </motion.div>
      </motion.header>

      {/* =====================================================
          MAIN
          ===================================================== */}

      <main className="contact-page__main">
        <section className="contact-section">
          {/* =================================================
              BACKGROUND
              ================================================= */}

          <div className="contact-section__background" aria-hidden="true">
            <div
              className="
                contact-section__glow
                contact-section__glow--one
              "
            />

            <div
              className="
                contact-section__glow
                contact-section__glow--two
              "
            />

            <div className="contact-section__grid" />
          </div>

          <div className="contact-section__inner">
            {/* =================================================
                HERO
                ================================================= */}

            <motion.header
              className="contact-section__hero"
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,

                amount: 0.1,
              }}
              variants={revealVariants}
            >
              <h1 className="contact-section__title">
                {t("contact.title.prefix", "How can we")}{" "}
                <strong>{t("contact.title.highlight", "help?")}</strong>
              </h1>

              <p className="contact-section__lead">
                {t(
                  "contact.description",
                  "Find the right LEG-UP contact for general platform support or for administrative requests related to your organisation.",
                )}
              </p>
            </motion.header>

            {/* =================================================
                CONTACT CARDS
                ================================================= */}

            <div className="contact-cards">
              {/* ===============================================
                  GENERAL
                  =============================================== */}

              <motion.article
                className="
                  contact-info-card
                  contact-info-card--general
                "
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={liveViewport}
                variants={cardVariants}
              >
                <div className="contact-info-card__top">
                  <span className="contact-info-card__icon">
                    <Headphones size={25} strokeWidth={1.8} />
                  </span>

                  <span className="contact-info-card__number">01</span>
                </div>

                <span className="contact-info-card__eyebrow">
                  {t("contact.cards.general.eyebrow", "GENERAL SUPPORT")}
                </span>

                <h2>
                  {t(
                    "contact.cards.general.title",
                    "Questions about the LEG-UP platform?",
                  )}
                </h2>

                <p className="contact-info-card__intro">
                  {t(
                    "contact.cards.general.description",
                    "If you need assistance using LEG-UP, have a question about your account or encounter a technical issue, you can contact the platform team directly.",
                  )}
                </p>

                <div className="contact-info-card__details">
                  <div className="contact-detail">
                    <span className="contact-detail__icon">
                      <Headphones size={18} />
                    </span>

                    <div>
                      <span className="contact-detail__label">
                        {t(
                          "contact.cards.general.supportLabel",
                          "Technical support",
                        )}
                      </span>

                      <p>
                        {t(
                          "contact.cards.general.supportDescription",
                          "For login problems, account access, technical issues or unexpected platform behaviour.",
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="contact-detail">
                    <span className="contact-detail__icon">
                      <ShieldCheck size={18} />
                    </span>

                    <div>
                      <span className="contact-detail__label">
                        {t(
                          "contact.cards.general.adminLabel",
                          "Platform administration",
                        )}
                      </span>

                      <p>
                        {t(
                          "contact.cards.general.adminDescription",
                          "For organisation configuration, participation and other administrative requests.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="contact-info-card__emails">
                  <a href={`mailto:${SUPPORT_EMAIL}`}>
                    <Mail size={17} />

                    <span>{SUPPORT_EMAIL}</span>

                    <span className="contact-info-card__email-arrow">↗</span>
                  </a>

                  <a href={`mailto:${ADMIN_EMAIL}`}>
                    <Mail size={17} />

                    <span>{ADMIN_EMAIL}</span>

                    <span className="contact-info-card__email-arrow">↗</span>
                  </a>
                </div>
              </motion.article>

              {/* ===============================================
                  LENDER
                  =============================================== */}

              <motion.article
                className="
                  contact-info-card
                  contact-info-card--lender
                "
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={liveViewport}
                variants={cardVariants}
              >
                <div className="contact-info-card__top">
                  <span className="contact-info-card__icon">
                    <Building2 size={25} strokeWidth={1.8} />
                  </span>

                  <span className="contact-info-card__number">02</span>
                </div>

                <span className="contact-info-card__eyebrow">
                  {t("contact.cards.lender.eyebrow", "FOR LENDERS")}
                </span>

                <h2>
                  {t(
                    "contact.cards.lender.title",
                    "Complete your organisation setup after registration",
                  )}
                </h2>

                <p className="contact-info-card__intro">
                  {t(
                    "contact.cards.lender.description",
                    "Once your Lender organisation has been registered in LEG-UP, some platform configuration is completed by the Platform Administrator.",
                  )}
                </p>

                <div className="contact-info-card__tasks">
                  <div className="contact-task">
                    <span className="contact-task__icon">
                      <MapPinned size={19} />
                    </span>

                    <div>
                      <strong>
                        {t(
                          "contact.cards.lender.regionsTitle",
                          "Market region assignment",
                        )}
                      </strong>

                      <p>
                        {t(
                          "contact.cards.lender.regionsDescription",
                          "Contact the Platform Administrator to request the market regions in which your Lender organisation participates.",
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="contact-task">
                    <span className="contact-task__icon">
                      <Settings2 size={19} />
                    </span>

                    <div>
                      <strong>
                        {t(
                          "contact.cards.lender.fundingTitle",
                          "Funding types",
                        )}
                      </strong>

                      <p>
                        {t(
                          "contact.cards.lender.fundingDescription",
                          "The Platform Administrator can create or configure the funding types offered by your organisation.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="contact-info-card__include">
                  <span>
                    {t("contact.cards.include", "WHEN CONTACTING US")}
                  </span>

                  <div>
                    <Check size={15} />

                    <p>
                      {t(
                        "contact.cards.lender.include",
                        "Include your organisation name, registered account email, requested market regions and the funding types that should be configured.",
                      )}
                    </p>
                  </div>
                </div>

                <a
                  href={`mailto:${ADMIN_EMAIL}`}
                  className="contact-info-card__primary-email"
                >
                  <Mail size={17} />

                  <span>{ADMIN_EMAIL}</span>

                  <span>↗</span>
                </a>
              </motion.article>

              {/* ===============================================
                  OSS
                  =============================================== */}

              <motion.article
                className="
                  contact-info-card
                  contact-info-card--oss
                "
                custom={2}
                initial="hidden"
                whileInView="visible"
                viewport={liveViewport}
                variants={cardVariants}
              >
                <div className="contact-info-card__top">
                  <span className="contact-info-card__icon">
                    <Wrench size={25} strokeWidth={1.8} />
                  </span>

                  <span className="contact-info-card__number">03</span>
                </div>

                <span className="contact-info-card__eyebrow">
                  {t("contact.cards.oss.eyebrow", "FOR ONE-STOP-SHOPS")}
                </span>

                <h2>
                  {t(
                    "contact.cards.oss.title",
                    "Keep your regional coverage up to date",
                  )}
                </h2>

                <p className="contact-info-card__intro">
                  {t(
                    "contact.cards.oss.description",
                    "Your OSS defines its operational coverage during onboarding. If this coverage changes later, the Platform Administrator can update it for your organisation.",
                  )}
                </p>

                <div className="contact-info-card__tasks">
                  <div className="contact-task">
                    <span className="contact-task__icon">
                      <MapPinned size={19} />
                    </span>

                    <div>
                      <strong>
                        {t(
                          "contact.cards.oss.coverageTitle",
                          "Update OSS coverage",
                        )}
                      </strong>

                      <p>
                        {t(
                          "contact.cards.oss.coverageDescription",
                          "Contact the Platform Administrator when regions or postal codes need to be added, removed or corrected.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="contact-info-card__include">
                  <span>
                    {t("contact.cards.include", "WHEN CONTACTING US")}
                  </span>

                  <div>
                    <Check size={15} />

                    <p>
                      {t(
                        "contact.cards.oss.include",
                        "Include your organisation name, registered account email and the regions or postal codes that should be added, removed or corrected.",
                      )}
                    </p>
                  </div>
                </div>

                <a
                  href={`mailto:${ADMIN_EMAIL}`}
                  className="contact-info-card__primary-email"
                >
                  <Mail size={17} />

                  <span>{ADMIN_EMAIL}</span>

                  <span>↗</span>
                </a>
              </motion.article>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
